import { useState } from 'react';
import { Box, Center, Text, VStack, useColorModeValue, Code as ChakraCode } from '@chakra-ui/react';
import MiniCalendar from 'components/calendar/MiniCalendar';

type Value = Date | null | [Date | null, Date | null];

const MiniCalendarPreview = () => {
  const [selectedSmall, setSelectedSmall] = useState<Value>(new Date());
  const [selectedMedium, setSelectedMedium] = useState<Value>(new Date());
  const [selectedLarge, setSelectedLarge] = useState<Value>(new Date());

  const bg = useColorModeValue('gray.50', 'gray.900');
  const textColor = useColorModeValue('gray.800', 'white');
  const codeBg = useColorModeValue('gray.100', 'gray.800');

  const formatDate = (value: Value): string => {
    if (value instanceof Date) {
      return value.toLocaleDateString('es-ES');
    }
    if (Array.isArray(value)) {
      const start = value[0]?.toLocaleDateString('es-ES') || '---';
      const end = value[1]?.toLocaleDateString('es-ES') || '---';
      return `${start} - ${end}`;
    }
    return 'No seleccionado';
  };

  return (
    <Center minH="100vh" bg={bg} p="40px">
      <VStack spacing="56px" w="100%" maxW="1000px">
        {/* Header */}
        <VStack spacing="8px" align="flex-start" w="100%">
          <Text fontSize="2xl" fontWeight="700" color={textColor}>
            MiniCalendar — Preview
          </Text>
          <Text fontSize="sm" color="gray.500">
            Componente de calendario en miniatura con control de tamaño
          </Text>
        </VStack>

        <Box w="100%" h="1px" bg="gray.200" />

        {/* Tamaño Small */}
        <VStack spacing="16px" align="flex-start" w="100%">
          <Text fontSize="sm" fontWeight="700" color="gray.600">
            📱 Tamaño Small (sm)
          </Text>
          <Box display="flex" justifyContent="center" w="100%">
            <Box borderWidth="1px" borderColor="gray.200" borderRadius="12px" p="4" bg={useColorModeValue('white', 'gray.800')}>
              <MiniCalendar
                size="sm"
                value={selectedSmall}
                onChange={setSelectedSmall}
              />
            </Box>
          </Box>
          {selectedSmall && (
            <Text fontSize="xs" color="gray.500" textAlign="center" w="100%">
              Seleccionado: {formatDate(selectedSmall)}
            </Text>
          )}
        </VStack>

        <Box w="100%" h="1px" bg="gray.200" />

        {/* Tamaño Medium (Default) */}
        <VStack spacing="16px" align="flex-start" w="100%">
          <Text fontSize="sm" fontWeight="700" color="gray.600">
            🔷 Tamaño Medium (md) - Default
          </Text>
          <Box display="flex" justifyContent="center" w="100%">
            <Box borderWidth="1px" borderColor="gray.200" borderRadius="12px" p="4" bg={useColorModeValue('white', 'gray.800')}>
              <MiniCalendar
                size="md"
                value={selectedMedium}
                onChange={setSelectedMedium}
              />
            </Box>
          </Box>
          {selectedMedium && (
            <Text fontSize="xs" color="gray.500" textAlign="center" w="100%">
              Seleccionado: {formatDate(selectedMedium)}
            </Text>
          )}
        </VStack>

        <Box w="100%" h="1px" bg="gray.200" />

        {/* Tamaño Large */}
        <VStack spacing="16px" align="flex-start" w="100%">
          <Text fontSize="sm" fontWeight="700" color="gray.600">
            📲 Tamaño Large (lg)
          </Text>
          <Box display="flex" justifyContent="center" w="100%">
            <Box borderWidth="1px" borderColor="gray.200" borderRadius="12px" p="4" bg={useColorModeValue('white', 'gray.800')}>
              <MiniCalendar
                size="lg"
                value={selectedLarge}
                onChange={setSelectedLarge}
              />
            </Box>
          </Box>
          {selectedLarge && (
            <Text fontSize="xs" color="gray.500" textAlign="center" w="100%">
              Seleccionado: {formatDate(selectedLarge)}
            </Text>
          )}
        </VStack>

        <Box w="100%" h="1px" bg="gray.200" />

        {/* Props Documentation */}
        <VStack spacing="12px" align="flex-start" w="100%">
          <Text fontSize="sm" fontWeight="600" color="gray.600">
            📋 Props disponibles:
          </Text>
          <VStack spacing="6px" align="flex-start" fontSize="xs" color="gray.500" w="100%">
            <Text>• <ChakraCode>size</ChakraCode>: Tamaño: "sm" | "md" | "lg" (default: "md")</Text>
            <Text>• <ChakraCode>value</ChakraCode>: Fecha o rango seleccionado (opcional)</Text>
            <Text>• <ChakraCode>onChange</ChakraCode>: Callback cuando cambia la selección (opcional)</Text>
            <Text>• <ChakraCode>selectRange</ChakraCode>: Permitir rango de fechas (default: false)</Text>
          </VStack>
        </VStack>

        <VStack spacing="12px" align="flex-start" w="100%">
          <Text fontSize="sm" fontWeight="600" color="gray.600">
            ✨ Tamaños:
          </Text>
          <VStack spacing="6px" align="flex-start" fontSize="xs" color="gray.500" w="100%">
            <Text>✓ <ChakraCode>sm</ChakraCode>: Padding 12px 10px, border-radius 16px</Text>
            <Text>✓ <ChakraCode>md</ChakraCode>: Padding 20px 15px, border-radius 20px (default)</Text>
            <Text>✓ <ChakraCode>lg</ChakraCode>: Padding 28px 20px, border-radius 24px</Text>
          </VStack>
        </VStack>

        <VStack spacing="12px" align="flex-start" w="100%">
          <Text fontSize="sm" fontWeight="600" color="gray.600">
            💡 Ejemplo de uso:
          </Text>
          <Box bg={codeBg} p="12px" borderRadius="md" w="100%" fontSize="xs" fontFamily="mono" overflowX="auto">
            <Text>{`<MiniCalendar
  size="md"
  value={selectedDate}
  onChange={setSelectedDate}
  selectRange={false}
/>`}</Text>
          </Box>
        </VStack>
      </VStack>
    </Center>
  );
};

export default MiniCalendarPreview;
